import { supabase } from '../lib/supabase';

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  original_price?: number;
  category_id?: string;
  subcategory_id?: string;
  seller_id: string;
  company_id?: string; // Reference to company for job listings
  auction_id?: string; // Reference to auction if product is sold via auction
  images: string[];
  colors: string[];
  custom_colors: string[];
  size_system?: string;
  sizes: string[];
  condition: 'new' | 'like_new' | 'good' | 'fair' | 'poor';
  brand?: string;
  model?: string;
  sku?: string;
  weight?: number;
  dimensions?: {
    length: number;
    width: number;
    height: number;
  };
  tags: string[];
  delivery_option_ids?: string[];
  is_active: boolean;
  is_featured: boolean;
  is_sold: boolean;
  is_job: boolean;
  view_count: number;
  favorite_count: number;
  created_at: string;
  updated_at: string;
  // Location coordinates
  latitude?: number;
  longitude?: number;
  location_name?: string;

  // Boost / visibility upsells (stored as JSON array in DB)
  boost?: any[];
}

export interface ProductImage {
  id: string;
  product_id: string;
  image_url: string;
  image_path: string;
  alt_text?: string;
  sort_order: number;
  is_primary: boolean;
  file_size?: number;
  mime_type?: string;
  width?: number;
  height?: number;
  created_at: string;
}

export interface CreateProductData {
  name: string;
  description: string;
  price: number;
  original_price?: number;
  stock_quantity?: number;
  category_id?: string;
  subcategory_id?: string;
  categories?: string[];
  is_active?: boolean;
  is_featured?: boolean;
  product_status?: string;
  company_id?: string; // Reference to company for job listings
  auction_id?: string; // Reference to auction if product is sold via auction
  images: string[];
  colors: string[];
  custom_colors: string[];
  size_system?: string;
  sizes: string[];
  condition?: 'new' | 'like_new' | 'good' | 'fair' | 'poor';
  brand?: string;
  model?: string;
  sku?: string;
  weight?: number;
  dimensions?: {
    length: number;
    width: number;
    height: number;
  };
  tags?: string[];
  delivery_option_ids?: string[];
  is_job?: boolean;
  // New fields
  product_type?: 'd' | 'a' | 'p';
  is_negotiable?: boolean;
  
  // Job-specific fields (only used when is_job = true)
  salary_type?: 'hourly' | 'fixed' | 'negotiable';
  location?: string;
  job_type?: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship' | 'temporary' | 'volunteer';
  experience_level?: 'entry-level' | 'mid-level' | 'senior-level' | 'executive';
  duration?: string;
  remote_work_allowed?: boolean;
  required_skills?: string[];
  job_requirements?: string[];
  benefits_perks?: string[];
  contact_email?: string;
  application_deadline?: string;
  expected_start_date?: string;
  // Location coordinates
  latitude?: number;
  longitude?: number;
  location_name?: string;
  // Backblaze image URLs (when uploading to Backblaze)
  backblaze_images?: string[];

  // Boost / visibility upsells (stored as JSON array in DB)
  boost?: any[];
}

export interface UpdateProductData {
  name?: string;
  description?: string;
  price?: number;
  original_price?: number;
  stock_quantity?: number;
  category_id?: string;
  subcategory_id?: string;
  categories?: string[];
  company_id?: string;
  auction_id?: string;
  images?: string[];
  backblaze_images?: string[];
  colors?: string[];
  custom_colors?: string[];
  size_system?: string;
  sizes?: string[];
  condition?: 'new' | 'like_new' | 'good' | 'fair' | 'poor';
  brand?: string;
  model?: string;
  sku?: string;
  weight?: number;
  dimensions?: {
    length: number;
    width: number;
    height: number;
  };
  tags?: string[];
  delivery_option_ids?: string[];
  is_job?: boolean;
  is_active?: boolean;
  is_featured?: boolean;
  is_sold?: boolean;
  view_count?: number;
  favorite_count?: number;
  product_status?: string; // e.g., 'WIP', 'ACTIVE'
  // New fields
  product_type?: 'd' | 'a' | 'p';
  is_negotiable?: boolean;
  // Job-specific fields
  salary_type?: 'hourly' | 'fixed' | 'negotiable';
  location?: string;
  job_type?: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship' | 'temporary' | 'volunteer';
  experience_level?: 'entry-level' | 'mid-level' | 'senior-level' | 'executive';
  duration?: string;
  remote_work_allowed?: boolean;
  required_skills?: string[];
  job_requirements?: string[];
  benefits_perks?: string[];
  contact_email?: string;
  application_deadline?: string;
  expected_start_date?: string;
  // Location coordinates
  latitude?: number;
  longitude?: number;
  location_name?: string;

  // Boost / visibility upsells (stored as JSON array in DB)
  boost?: any[];
}

export class ProductService {
  // Create minimal WIP product row and return it
  static async createWipProduct(params?: { product_type?: 'd' | 'a' | 'p'; is_negotiable?: boolean }): Promise<Product> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User must be authenticated to create products');
    const { data, error } = await supabase
      .from('products')
      .insert({ seller_id: user.id, product_status: 'WIP', is_active: false, product_type: params?.product_type, is_negotiable: params?.is_negotiable ?? false })
      .select()
      .single();
    if (error) throw new Error(`Failed to create WIP product: ${error.message}`);
    return data as Product;
  }

  // Fetch multiple products by IDs (minimal fields for homepage sections)
  static async getProductsByIds(ids: string[]): Promise<Array<{ id: string; name: string; price: number; originalPrice?: number; image: string }>> {
    if (!Array.isArray(ids) || ids.length === 0) return [];
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id,name,price,original_price,images')
        .in('id', ids);
      if (error) throw error;
      const items = (data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        price: p.price,
        originalPrice: p.original_price || undefined,
        image: Array.isArray(p.images) && p.images.length > 0 ? p.images[0] : '/placeholder-product.jpg',
      }));
      // preserve incoming order
      const order = new Map(ids.map((id, i) => [id, i] as const));
      return items.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    } catch (e) {
      console.error('getProductsByIds failed', e);
      return [];
    }
  }

  // Fetch specific products by IDs with seller + auction enrichment (same shape as getProductsWithSellers output)
  static async getProductsWithSellersByIds(ids: string[]): Promise<any[]> {
    if (!Array.isArray(ids) || ids.length === 0) return [];
    try {
      const { data: products, error } = await supabase
        .from('products')
        .select(
          `
          *,
          category_join:category_id(name),
          subcategory_join:subcategory_id(name),
          company_join:company_id(id,name,logo_url,slug),
          auctions:auction_id(
            id,
            end_time,
            current_bid,
            bid_increment,
            status,
            reserve_price,
            created_at
          )
        `
        )
        .in('id', ids);

      if (error) throw error;
      const rows = ((products as any[]) || []).slice();

      const sellerIds = Array.from(new Set(rows.map((p) => p.seller_id).filter(Boolean)));
      let sellerInfo: Record<string, any> = {};
      if (sellerIds.length > 0) {
        try {
          const { data: sellers, error: sellerError } = await supabase
            .from('users')
            .select('id, alias, created_at')
            .in('id', sellerIds);
          if (!sellerError && sellers) {
            sellerInfo = (sellers as any[]).reduce((acc, s) => {
              acc[String(s.id)] = s;
              return acc;
            }, {} as Record<string, any>);
          }
        } catch (e) {
          console.error('Error fetching seller info (favorites by ids):', e);
        }
      }

      const companyIds = Array.from(
        new Set(
          (rows || [])
            .filter((p: any) => p?.is_job && p?.company_id)
            .map((p: any) => String(p.company_id))
        )
      );
      let companyInfo: Record<string, any> = {};
      if (companyIds.length > 0) {
        try {
          const { data: companies, error: companyError } = await supabase
            .from('companies')
            .select('id, name, slug, logo_url')
            .in('id', companyIds);
          if (!companyError && companies) {
            companyInfo = (companies || []).reduce((acc: Record<string, any>, c: any) => {
              acc[String(c.id)] = c;
              return acc;
            }, {} as Record<string, any>);
          }
        } catch (e) {
          console.error('Error fetching company info:', e);
        }
      }

      const auctionIds = Array.from(new Set(rows.map((p) => p.auction_id).filter(Boolean)));
      let bidCounts: Record<string, number> = {};
      if (auctionIds.length > 0) {
        try {
          const { data: bidData, error: bidError } = await supabase
            .from('auction_bids')
            .select('auction_id')
            .in('auction_id', auctionIds);
          if (!bidError && bidData) {
            bidCounts = (bidData as any[]).reduce((acc, bid) => {
              const aid = String((bid as any).auction_id);
              acc[aid] = (acc[aid] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);
          }
        } catch (e) {
          console.error('Error fetching bid counts (favorites by ids):', e);
        }
      }

      const transformed = await Promise.all((products || []).map(async (product: any) => {
        const seller = sellerInfo[String(product.seller_id)];
        const dbArray: string[] = Array.isArray((product as any)?.categories) ? (product as any).categories : [];
        const fallbackPaths: string[] = (() => {
          const main = (product as any)?.category_join?.name;
          const sub = (product as any)?.subcategory_join?.name;
          if (!main) return [];
          return [sub ? `${main} / ${sub}` : `${main}`];
        })();
        const paths: string[] = dbArray && dbArray.length > 0 ? dbArray : fallbackPaths;

        const baseProduct: any = {
          id: product.id,
          name: product.name,
          price: product.price,
          originalPrice: product.original_price,
          image: Array.isArray(product.images) && product.images.length > 0 ? product.images[0] : '/placeholder-product.jpg',
          images: Array.isArray(product.images) ? product.images : (product.image ? [product.image] : []),
          rating: Math.round((4.0 + Math.random() * 1.0) * 10) / 10,
          reviewCount: Math.floor(Math.random() * 1000),
          category: (product as any)?.category_join?.name || 'Unknown',
          ...(paths && paths.length > 0 ? { categories: paths } : {}),
          sellerId: product.seller_id,
          seller: {
            name: seller?.alias || 'Unknown Seller',
            avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
            location: product.location || 'Unknown Location',
          },
          isNew: product.created_at ? new Date(product.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) : false,
          isFeatured: product.is_featured || false,
          discount: product.original_price ? Math.round(((product.original_price - product.price) / product.original_price) * 100) : undefined,
          description: product.description || '',
          tags: product.tags || [],
          inStock: !product.is_sold,
          condition: product.condition || 'new',
          brand: product.brand || 'Unknown',
          isJob: product.is_job || false,
          is_private_singlebuy: (product.is_private_singlebuy ?? false) === true,
          productType: (product as any).product_type,
          isNegotiable: ((product as any).is_negotiable ?? false) === true,
          latitude: (product as any).latitude ?? undefined,
          longitude: (product as any).longitude ?? undefined,
          location_name: (product as any).location_name ?? undefined,
        };

        if (product.auction_id) {
          const auction = (product as any).auctions;
          const bidCount = bidCounts[String(product.auction_id)] || 0;
          if (auction) {
            return {
              ...baseProduct,
              auctionId: product.auction_id,
              auction: {
                id: auction.id,
                end_time: auction.end_time,
                current_bid: auction.current_bid || product.price,
                bid_increment: auction.bid_increment || 5.0,
                status: auction.status || 'active',
                reserve_price: auction.reserve_price,
                bid_count: bidCount,
                highest_bidder: bidCount > 0 ? { name: 'Bidder' + Math.floor(Math.random() * 100), avatar: '/placeholder-avatar.jpg' } : undefined,
              },
            };
          }
          return {
            ...baseProduct,
            auctionId: product.auction_id,
            auction: {
              id: product.auction_id,
              end_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
              current_bid: product.price,
              bid_increment: 5.0,
              status: 'active',
              reserve_price: product.price * 1.5,
              bid_count: bidCount,
              highest_bidder: undefined,
            },
          };
        }

        if (product.is_job) {
          const companyId = product.company_id ? String(product.company_id) : '';
          let companyRow: any = companyId ? companyInfo[companyId] : null;
          if (!companyRow && companyId) {
            try {
              const { data: cRow, error: cErr } = await supabase
                .from('companies')
                .select('id, name, slug, logo_url')
                .eq('id', companyId)
                .single();
              if (!cErr && cRow) {
                companyInfo[companyId] = cRow;
                companyRow = cRow;
              }
            } catch (_) {
              // ignore
            }
          }

          const companyName: string | null = companyRow?.name || null;
          const companyLogo: string | null = companyRow?.logo_url || null;
          return {
            ...baseProduct,
            company: companyName || seller?.alias || 'Unknown Company',
            companyLogo: companyLogo || undefined,
            location: product.location || 'Location not specified',
            salary: product.price
              ? {
                  min: product.price,
                  max: product.price,
                  currency: 'USD',
                }
              : undefined,
            employmentType: product.job_type || 'full-time',
            experienceLevel: product.experience_level || 'mid-level',
            isRemote: product.remote_work_allowed || false,
            postedDate: product.created_at || new Date().toISOString(),
            requirements: product.required_skills || [],
            benefits: product.benefits_perks || [],
            duration: product.duration || undefined,
            applicantsCount: Math.floor(Math.random() * 50),
            viewsCount: Math.floor(Math.random() * 200),
            isUrgent: product.application_deadline ? new Date(product.application_deadline) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) : false,
          };
        }

        return baseProduct;
      }));

      const byId = new Map<string, any>();
      transformed.forEach((p: any) => byId.set(String(p.id), p));
      return ids.map((id) => byId.get(String(id))).filter(Boolean);
    } catch (e) {
      console.error('getProductsWithSellersByIds failed', e);
      return [];
    }
  }

  // Upload image to Supabase Storage
  static async uploadImage(
    file: File,
    userId: string,
    productId?: string
  ): Promise<{ url: string; path: string }> {
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Error uploading image:', error);
        throw new Error(`Failed to upload image: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('product-images')
        .getPublicUrl(fileName);

      return {
        url: urlData.publicUrl,
        path: fileName
      };
    } catch (error) {
      console.error('Error in uploadImage:', error);
      throw error;
    }
  }

  // Upload multiple images
  static async uploadImages(
    files: File[],
    userId: string,
    productId?: string
  ): Promise<{ url: string; path: string }[]> {
    try {
      const uploadPromises = files.map(file => this.uploadImage(file, userId, productId));
      const results = await Promise.all(uploadPromises);
      return results;
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  }

  // Convert data URL to File
  static dataURLtoFile(dataURL: string, filename: string): File {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  // Create a new product (images already uploaded to storage)
  static async createProductDirect(productData: CreateProductData): Promise<Product> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to create products');
      }

      const resolvedIsActive = productData.is_active ?? (productData.is_job ? true : false);

      // Create product record directly (images are already Supabase Storage URLs)
      const insertData = {
          name: productData.name,
          description: productData.description,
          price: productData.price,
          original_price: productData.original_price,
          stock_quantity: productData.stock_quantity,
          // Legacy fields no longer used
          category_id: undefined,
          subcategory_id: undefined,
          // New categories array field
          categories: productData.categories || [],
          // Activation & status
          is_active: resolvedIsActive,
          product_status: productData.product_status ?? (resolvedIsActive ? 'ACTIVE' : 'INACTIVE'),
          company_id: productData.company_id, // Include company_id for job listings
          auction_id: productData.auction_id, // Include auction_id for auction products
          seller_id: user.id,
          images: productData.images,
          colors: productData.colors,
          custom_colors: productData.custom_colors,
          size_system: productData.size_system,
          sizes: productData.sizes,
          condition: productData.condition || 'new',
          brand: productData.brand,
          model: productData.model,
          sku: productData.sku,
          weight: productData.weight,
          dimensions: productData.dimensions,
          tags: productData.tags || [],
          delivery_option_ids: productData.delivery_option_ids || [],
          is_job: productData.is_job || false,
          product_type: productData.product_type,
          is_negotiable: productData.is_negotiable,
          // Job-specific fields
          salary_type: productData.salary_type,
          location: productData.location,
          job_type: productData.job_type,
          experience_level: productData.experience_level,
          duration: productData.duration,
          remote_work_allowed: productData.remote_work_allowed,
          required_skills: productData.required_skills,
          job_requirements: productData.job_requirements,
          benefits_perks: productData.benefits_perks,
          contact_email: productData.contact_email,
          application_deadline: productData.application_deadline,
          expected_start_date: productData.expected_start_date,
          // Location coordinates
          latitude: productData.latitude,
          longitude: productData.longitude,
          location_name: productData.location_name,
          backblaze_images: productData.backblaze_images || [],
          boost: Array.isArray(productData.boost) ? productData.boost : [],
        };

      console.log('🗄️ Database insert data:', insertData);

      const { data: product, error: productError } = await supabase
        .from('products')
        .insert(insertData)
        .select()
        .single();

      if (productError) {
        console.error('❌ Supabase error creating product:', productError);
        
        // Enhanced error handling for Supabase errors
        let errorMessage = `Failed to create product: ${productError.message}`;
        
        // Check for specific Supabase error codes
        if (productError.code === '23505') {
          errorMessage = 'Duplicate entry: A product with this information already exists';
        } else if (productError.code === '23503') {
          errorMessage = 'Foreign key constraint failed: Invalid reference to related data';
        } else if (productError.code === '23502') {
          errorMessage = 'Required field missing: Please fill in all required fields';
        } else if (productError.code === '42501') {
          errorMessage = 'Permission denied: You do not have permission to create products';
        } else if (productError.code === 'PGRST116') {
          errorMessage = 'Row Level Security policy violation: Access denied';
        }
        
        // Log detailed error information
        console.error('📋 Supabase Error Details:', {
          code: productError.code,
          message: productError.message,
          details: productError.details,
          hint: productError.hint,
          insertData,
          timestamp: new Date().toISOString()
        });
        
        throw new Error(errorMessage);
      }

      // Create product image records for metadata
      if (productData.images.length > 0) {
        const imageRecords = productData.images.map((url, index) => {
          // Extract path from Supabase Storage URL
          const urlParts = url.split('/storage/v1/object/public/product-images/');
          const imagePath = urlParts.length > 1 ? urlParts[1] : '';
          
          return {
            product_id: product.id,
            image_url: url,
            image_path: imagePath,
            alt_text: `${productData.name} - Image ${index + 1}`,
            sort_order: index,
            is_primary: index === 0,
          };
        });

        const { error: imagesError } = await supabase
          .from('product_images')
          .insert(imageRecords);

        if (imagesError) {
          console.error('Error creating product images:', imagesError);
          // Don't throw error here, product was created successfully
        }
      }

      return product;
    } catch (error) {
      console.error('Error in createProductDirect:', error);
      throw error;
    }
  }

  // Create a new product (with image upload from data URLs)
  static async createProduct(productData: CreateProductData): Promise<Product> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to create products');
      }

      // Upload images if they are data URLs
      const imageUrls: string[] = [];
      const imagePaths: string[] = [];

      for (let i = 0; i < productData.images.length; i++) {
        const imageUrl = productData.images[i];
        
        if (imageUrl.startsWith('data:')) {
          // Convert data URL to file and upload
          const file = this.dataURLtoFile(imageUrl, `product-image-${i}.jpg`);
          const uploadResult = await this.uploadImage(file, user.id);
          imageUrls.push(uploadResult.url);
          imagePaths.push(uploadResult.path);
        } else {
          // Already a URL, use as is
          imageUrls.push(imageUrl);
          imagePaths.push(''); // No path for external URLs
        }
      }

      // Create product record
      const { data: product, error: productError } = await supabase
        .from('products')
        .insert({
          name: productData.name,
          description: productData.description,
          price: productData.price,
          original_price: productData.original_price,
          // Legacy fields no longer used
          category_id: undefined,
          subcategory_id: undefined,
          categories: productData.categories || [],
          is_active: productData.is_active ?? false,
          product_status: productData.product_status ?? ((productData.is_active ?? false) ? 'ACTIVE' : 'INACTIVE'),
          seller_id: user.id,
          images: imageUrls,
          colors: productData.colors,
          custom_colors: productData.custom_colors,
          size_system: productData.size_system,
          sizes: productData.sizes,
          condition: productData.condition || 'new',
          brand: productData.brand,
          model: productData.model,
          sku: productData.sku,
          weight: productData.weight,
          dimensions: productData.dimensions,
          tags: productData.tags || [],
          boost: Array.isArray(productData.boost) ? productData.boost : [],
        })
        .select()
        .single();

      if (productError) {
        console.error('Error creating product:', productError);
        throw new Error(`Failed to create product: ${productError.message}`);
      }

      // Create product image records
      if (imagePaths.some(path => path)) {
        const imageRecords = imageUrls.map((url, index) => ({
          product_id: product.id,
          image_url: url,
          image_path: imagePaths[index] || '',
          alt_text: `${productData.name} - Image ${index + 1}`,
          sort_order: index,
          is_primary: index === 0,
        }));

        const { error: imagesError } = await supabase
          .from('product_images')
          .insert(imageRecords);

        if (imagesError) {
          console.error('Error creating product images:', imagesError);
          // Don't throw error here, product was created successfully
        }
      }

      return product;
    } catch (error) {
      console.error('Error in createProduct:', error);
      throw error;
    }
  }

  // Get products with pagination and filters
  static async getProducts(options: {
    page?: number;
    limit?: number;
    categoryId?: string;
    subcategoryId?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
    sortBy?: 'created_at' | 'price' | 'name' | 'view_count' | 'favorite_count';
    sortOrder?: 'asc' | 'desc';
    isActive?: boolean;
  } = {}): Promise<{ products: Product[]; total: number }> {
    try {
      const {
        page = 1,
        limit = 20,
        categoryId,
        subcategoryId,
        search,
        minPrice,
        maxPrice,
        sortBy = 'created_at',
        sortOrder = 'desc',
        isActive = true
      } = options;

      let query = supabase
        .from('products')
        .select('*', { count: 'exact' });

      // Apply filters
      if (isActive !== undefined) {
        query = query.eq('is_active', isActive);
      }

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      if (subcategoryId) {
        query = query.eq('subcategory_id', subcategoryId);
      }

      if (search) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      if (minPrice !== undefined) {
        query = query.gte('price', minPrice);
      }

      if (maxPrice !== undefined) {
        query = query.lte('price', maxPrice);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data: products, error, count } = await query;

      if (error) {
        console.error('Error fetching products:', error);
        throw new Error(`Failed to fetch products: ${error.message}`);
      }

      return {
        products: products || [],
        total: count || 0
      };
    } catch (error) {
      console.error('Error in getProducts:', error);
      throw error;
    }
  }

  // Get products with seller information for the AllProductsPage
  static async getProductsWithSellers(options: {
    page?: number;
    limit?: number;
    categoryId?: string;
    subcategoryId?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
    companyId?: string;
    // Filter by product tags (array of tag strings). When provided, results will
    // include products whose tags overlap with any of the provided values.
    tags?: string[];
    sortBy?: 'created_at' | 'price' | 'name' | 'view_count' | 'favorite_count';
    sortOrder?: 'asc' | 'desc';
    isActive?: boolean;
    sellerAlias?: string;
    isJob?: boolean; // Add isJob filter
  } = {}): Promise<{ products: any[]; total: number }> {
    try {
      const {
        page = 1,
        limit = 20,
        categoryId,
        subcategoryId,
        search,
        minPrice,
        maxPrice,
        companyId,
        sortBy = 'created_at',
        sortOrder = 'desc',
        isActive = true,
        sellerAlias,
        isJob,
        tags
      } = options;

      let query = supabase
        .from('products')
        .select(
          `
          *,
          category_join:category_id(name),
          subcategory_join:subcategory_id(name),
          company_join:company_id(id,name,logo_url,slug),
          auctions:auction_id(
            id,
            end_time,
            current_bid,
            bid_increment,
            status,
            reserve_price,
            created_at
          )
        `, { count: 'exact' });

      // Apply filters
      if (isActive !== undefined) {
        query = query.eq('is_active', isActive);
      }

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      if (subcategoryId) {
        query = query.eq('subcategory_id', subcategoryId);
      }

      if (search) {
        query = query.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      if (minPrice !== undefined) {
        query = query.gte('price', minPrice);
      }

      if (maxPrice !== undefined) {
        query = query.lte('price', maxPrice);
      }

      // Apply tag filter (any overlap)
      if (tags && Array.isArray(tags) && tags.length > 0) {
        console.log('ProductService - Applying tag filter:', tags);
        console.log('ProductService - Normalized tags:', tags.map(tag => tag.toLowerCase()));
        try {
          // Use OR conditions to match products that have ANY of the provided tags
          // Format: tags.cs.{tag1},tags.cs.{tag2},etc.
          // Use case-insensitive matching by converting to lowercase
          const normalizedTags = tags.map(tag => tag.toLowerCase());
          const tagConditions = normalizedTags.map(tag => `tags.cs.{${tag}}`).join(',');
          query = query.or(tagConditions);
          console.log('ProductService - Using OR conditions for tag matching:', tagConditions);
        } catch (e) {
          console.error('Error applying tag filter with OR conditions:', e);
          // Fallback: contains all tags (not ideal but functional)
          query = query.contains('tags', tags.map(tag => tag.toLowerCase()));
          console.log('ProductService - Fallback to contains all tags');
        }
      }

      // Apply seller filter at database level if specified
      if (sellerAlias) {
        // First get the seller ID by alias
        const { data: sellerData, error: sellerError } = await supabase
          .from('users')
          .select('id')
          .eq('alias', sellerAlias)
          .single();
        
        if (sellerError || !sellerData) {
          // If seller not found, return empty results
          return { products: [], total: 0 };
        }
        
        query = query.eq('seller_id', sellerData.id);
      }

      // Apply job/product filter at database level if specified
      if (isJob !== undefined) {
        query = query.eq('is_job', isJob);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });

      // Apply pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to);

      const { data: products, error, count } = await query;

      if (error) {
        console.error('Error fetching products with sellers:', error);
        throw new Error(`Failed to fetch products: ${error.message}`);
      }

      console.log(`ProductService - Query result: ${products?.length || 0} products, count: ${count}, page: ${page}, limit: ${limit}`);

      // Get seller information for all products
      const sellerIds = Array.from(new Set((products || []).map(p => p.seller_id)));
      let sellerInfo: Record<string, any> = {};
      
      if (sellerIds.length > 0) {
        try {
          const { data: sellers, error: sellerError } = await supabase
            .from('users')
            .select('id, alias, created_at')
            .in('id', sellerIds);
          
          if (!sellerError && sellers) {
            sellerInfo = sellers.reduce((acc, seller) => {
              acc[seller.id] = seller;
              return acc;
            }, {} as Record<string, any>);
          }
        } catch (error) {
          console.error('Error fetching seller info:', error);
        }
      }

      // Get company information for job listings
      const companyIds = Array.from(
        new Set(
          (products || [])
            .filter((p: any) => p?.is_job && p?.company_id)
            .map((p: any) => String(p.company_id))
        )
      );
      let companyInfo: Record<string, any> = {};
      if (companyIds.length > 0) {
        try {
          const { data: companies, error: companyError } = await supabase
            .from('companies')
            .select('id, name, slug, logo_url')
            .in('id', companyIds);
          if (!companyError && companies) {
            companyInfo = (companies || []).reduce((acc: Record<string, any>, c: any) => {
              acc[String(c.id)] = c;
              return acc;
            }, {} as Record<string, any>);
          }
        } catch (e) {
          console.error('Error fetching company info:', e);
        }
      }

      // Get bid counts for auction products
      const auctionIds = Array.from(new Set((products || [])
        .filter(p => p.auction_id && (p as any).auctions)
        .map(p => p.auction_id)));
      
      let bidCounts: Record<string, number> = {};
      
      if (auctionIds.length > 0) {
        try {
          const { data: bidData, error: bidError } = await supabase
            .from('auction_bids')
            .select('auction_id')
            .in('auction_id', auctionIds);
          
          if (!bidError && bidData) {
            bidCounts = bidData.reduce((acc, bid) => {
              acc[bid.auction_id] = (acc[bid.auction_id] || 0) + 1;
              return acc;
            }, {} as Record<string, number>);
          }
        } catch (error) {
          console.error('Error fetching bid counts:', error);
        }
      }

      // Transform the data to match the expected format
      const transformedProducts = await Promise.all((products || []).map(async (product: any) => {
        const seller = sellerInfo[product.seller_id];
        // Build categories[] fallback from legacy joins when needed
        const dbArray: string[] = Array.isArray((product as any).categories) ? (product as any).categories : [];
        const fallbackPaths: string[] = (() => {
          const main = (product as any)?.category_join?.name;
          const sub = (product as any)?.subcategory_join?.name;
          if (!main) return [];
          return [sub ? `${main} / ${sub}` : `${main}`];
        })();
        const paths: string[] = dbArray && dbArray.length > 0 ? dbArray : fallbackPaths;

        const baseProduct = {
          id: product.id,
          name: product.name,
          price: product.price,
          originalPrice: product.original_price,
          image: product.images && product.images.length > 0 ? product.images[0] : '/placeholder-product.jpg',
          images: Array.isArray(product.images) ? product.images : (product.image ? [product.image] : []),
          rating: Math.round((4.0 + Math.random() * 1.0) * 10) / 10, // Placeholder rating rounded to 1 decimal
          reviewCount: Math.floor(Math.random() * 1000), // Placeholder review count
          // Keep legacy single category label for any UI still using it (from join alias)
          category: (product as any)?.category_join?.name || 'Unknown',
          // NEW: categories[] full-path array for beautiful labels (with fallback)
          ...(paths && paths.length > 0 ? { categories: paths } : {}),
          sellerId: product.seller_id,
          seller: {
            name: seller?.alias || 'Unknown Seller',
            avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
            location: product.location || 'Unknown Location'
          },
          isNew: product.created_at ? new Date(product.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) : false,
          isFeatured: product.is_featured || false,
          discount: product.original_price ? Math.round(((product.original_price - product.price) / product.original_price) * 100) : undefined,
          description: product.description || '',
          tags: product.tags || [],
          inStock: !product.is_sold,
          condition: product.condition || 'new',
          brand: product.brand || 'Unknown',
          isJob: product.is_job || false,
          is_private_singlebuy: (product.is_private_singlebuy ?? false) === true,
          productType: (product as any).product_type,
          isNegotiable: ((product as any).is_negotiable ?? false) === true,
          latitude: (product as any).latitude ?? undefined,
          longitude: (product as any).longitude ?? undefined,
          location_name: (product as any).location_name ?? undefined,
        };

        // Add auction data if this product has an auction
        if (product.auction_id) {
          if (product.auctions) {
            const auction = product.auctions;
            const bidCount = bidCounts[product.auction_id] || 0;
            
            return {
              ...baseProduct,
              auctionId: product.auction_id,
              auction: {
                id: auction.id,
                end_time: auction.end_time,
                current_bid: auction.current_bid || product.price,
                bid_increment: auction.bid_increment || 5.00,
                status: auction.status || 'active',
                reserve_price: auction.reserve_price,
                bid_count: bidCount,
                highest_bidder: bidCount > 0 ? {
                  name: 'Bidder' + Math.floor(Math.random() * 100),
                  avatar: '/placeholder-avatar.jpg'
                } : undefined
              }
            };
          } else {
            // Product has auction_id but no auction data - create a simple auction label
            return {
              ...baseProduct,
              auctionId: product.auction_id,
              auction: {
                id: product.auction_id,
                end_time: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
                current_bid: product.price,
                bid_increment: 5.00,
                status: 'active',
                reserve_price: product.price * 1.5,
                bid_count: 0,
                highest_bidder: undefined
              }
            };
          }
        }

        // Add job-specific fields if this is a job
        if (product.is_job) {
          const companyId = (product as any).company_id ? String((product as any).company_id) : '';
          let companyRow: any = companyId ? (companyInfo[companyId] || (product as any).company_join) : (product as any).company_join;
          if (!companyRow && companyId) {
            try {
              const { data: cRow, error: cErr } = await supabase
                .from('companies')
                .select('id, name, slug, logo_url')
                .eq('id', companyId)
                .single();
              if (!cErr && cRow) {
                companyInfo[companyId] = cRow;
                companyRow = cRow;
              }
            } catch (_) {
              // ignore
            }
          }

          const companyName: string | null = companyRow?.name || null;
          const companyLogo: string | null = companyRow?.logo_url || null;
          return {
            ...baseProduct,
            // Job-specific fields from the database
            company: companyName || seller?.alias || 'Unknown Company',
            companyLogo: companyLogo || undefined,
            location: product.location || 'Location not specified',
            salary: product.price ? {
              min: product.price,
              max: product.price,
              currency: 'USD'
            } : undefined,
            employmentType: product.job_type || 'full-time',
            experienceLevel: product.experience_level || 'mid-level',
            isRemote: product.remote_work_allowed || false,
            postedDate: product.created_at || new Date().toISOString(),
            requirements: product.required_skills || [],
            benefits: product.benefits_perks || [],
            duration: product.duration || undefined,
            applicantsCount: Math.floor(Math.random() * 50), // Placeholder
            viewsCount: Math.floor(Math.random() * 200), // Placeholder
            isUrgent: product.application_deadline ? new Date(product.application_deadline) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) : false,
          };
        }

        return baseProduct;
      }));

      // Seller filtering is now handled at database level before pagination


      return {
        products: transformedProducts,
        total: count || 0
      };
    } catch (error) {
      console.error('Error in getProductsWithSellers:', error);
      throw error;
    }
  }

  // Get product by ID
  static async getProductById(id: string): Promise<Product | null> {
    try {
      const { data: product, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Product not found
        }
        console.error('Error fetching product:', error);
        throw new Error(`Failed to fetch product: ${error.message}`);
      }

      return product;
    } catch (error) {
      console.error('Error in getProductById:', error);
      throw error;
    }
  }

  // Update product
  static async updateProduct(id: string, updates: UpdateProductData): Promise<Product> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to update products');
      }

      const { data: product, error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', id)
        .eq('seller_id', user.id) // Ensure user owns the product
        .select()
        .single();

      if (error) {
        console.error('Error updating product:', error);
        throw new Error(`Failed to update product: ${error.message}`);
      }

      return product;
    } catch (error) {
      console.error('Error in updateProduct:', error);
      throw error;
    }
  }

  // Delete product
  static async deleteProduct(id: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to delete products');
      }

      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id)
        .eq('seller_id', user.id); // Ensure user owns the product

      if (error) {
        console.error('Error deleting product:', error);
        throw new Error(`Failed to delete product: ${error.message}`);
      }
    } catch (error) {
      console.error('Error in deleteProduct:', error);
      throw error;
    }
  }

  // Increment view count
  static async incrementViewCount(id: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('increment_product_view_count', {
        product_uuid: id
      });

      if (error) {
        console.error('Error incrementing view count:', error);
        throw new Error(`Failed to increment view count: ${error.message}`);
      }
    } catch (error) {
      console.error('Error in incrementViewCount:', error);
      throw error;
    }
  }

  // Toggle favorite
  static async toggleFavorite(productId: string): Promise<{ isFavorited: boolean }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to toggle favorites');
      }

      // Check if already favorited
      const { data: existing } = await supabase
        .from('product_favorites')
        .select('id')
        .eq('product_id', productId)
        .eq('user_id', user.id)
        .single();

      if (existing) {
        // Remove from favorites
        const { error } = await supabase
          .from('product_favorites')
          .delete()
          .eq('product_id', productId)
          .eq('user_id', user.id);

        if (error) {
          throw new Error(`Failed to remove favorite: ${error.message}`);
        }

        return { isFavorited: false };
      } else {
        // Add to favorites
        const { error } = await supabase
          .from('product_favorites')
          .insert({
            product_id: productId,
            user_id: user.id
          });

        if (error) {
          throw new Error(`Failed to add favorite: ${error.message}`);
        }

        return { isFavorited: true };
      }
    } catch (error) {
      console.error('Error in toggleFavorite:', error);
      throw error;
    }
  }

  // Get user's products
  static async getUserProducts(userId?: string): Promise<Product[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const targetUserId = userId || user?.id;

      if (!targetUserId) {
        throw new Error('User ID is required');
      }

      const { data: products, error } = await supabase
        .from('products')
        .select('*')
        .eq('seller_id', targetUserId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user products:', error);
        throw new Error(`Failed to fetch user products: ${error.message}`);
      }

      return products || [];
    } catch (error) {
      console.error('Error in getUserProducts:', error);
      throw error;
    }
  }
}
